import {
  QuestionRuleInstance,
  SchedulerInterval,
} from '@jupiterone/jupiter-types';
import {
  useSnackbarNotification,
  useIntegrationsPollingIntervalValueSetEntitlements,
} from '@jupiterone/jupiter-web-toolkit';
import set from 'lodash/fp/set';
import { useEffect, useState } from 'react';
import { useCreateQuestionRuleInstance } from '../../../hooks/graphql/rule';

export interface HookProps {
  open: boolean;
  onClose: VoidFunction;
  query?: string;
  busy?: boolean;
}

const ruleTemplate: any = {
  name: '',
  description: '',
  specVersion: 1,
  pollingInterval: SchedulerInterval.ONE_DAY,
  question: {
    queries: [
      {
        name: 'query0',
        query:
          "Find DataStore with classification='critical' and encrypted=false as d return d.tag.AccountName as Account, d.displayName as UnencryptedDataStores, d._type as Type, d.encrypted as Encrypted",
        version: 'v1',
      },
    ],
  },
  operations: [
    {
      when: {
        type: 'FILTER',
        specVersion: 1,
        condition: ['AND', ['queries.query0.total', '>', 0]],
      },
      actions: [
        {
          type: 'SET_PROPERTY',
          targetValue: 'CRITICAL',
          targetProperty: 'alertLevel',
        },
        { type: 'CREATE_ALERT' },
      ],
    },
  ],
  outputs: ['alertLevel'],
};

const adjustRulePolling = (
  rule: QuestionRuleInstance,
  canUseOneDay: boolean
) => {
  return canUseOneDay
    ? rule
    : { ...rule, pollingInterval: SchedulerInterval.ONE_WEEK };
};

export const useCreateRuleModalLogic = ({
  query,
  busy,
  onClose,
}: HookProps) => {
  const [rule, setRule] = useState<QuestionRuleInstance>(ruleTemplate);
  const [
    mutateCreateRule,
    { loading: loadingCreateRule },
  ] = useCreateQuestionRuleInstance();
  const notification = useSnackbarNotification();

  const {
    data: integrationsPollingIntervalValueSetEntitlements,
  } = useIntegrationsPollingIntervalValueSetEntitlements();

  const hasAccessToPollingInterval = (increment: SchedulerInterval) => {
    if (!increment) {
      return false;
    }
    return !!integrationsPollingIntervalValueSetEntitlements?.checkValueSetEntitlementAccess.items.find(
      (item) => item.value === SchedulerInterval[increment] && item.canAccess
    );
  };

  useEffect(() => {
    const canUseDaily = hasAccessToPollingInterval(SchedulerInterval.ONE_DAY);
    setRule(adjustRulePolling(ruleTemplate, canUseDaily));
  }, []);

  useEffect(() => {
    if (query) {
      const newRule = set('question.queries[0].query', query, rule);
      setRule(newRule);
    }
  }, [query]);

  return {
    busy: busy ?? loadingCreateRule,
    rule,
    async handleSubmit(submittedRule: QuestionRuleInstance) {
      try {
        const submittedRuleWithCheckedPollingInterval = adjustRulePolling(
          submittedRule,
          hasAccessToPollingInterval(SchedulerInterval.ONE_DAY)
        );
        await mutateCreateRule(submittedRuleWithCheckedPollingInterval);
        notification.success('Alert rule created successfully');
        onClose();
      } catch (error) {
        notification.error({
          message: error.message,
          error,
        });
      }
    },
  };
};

const mockData = {
  busy: false,
  rule: ruleTemplate,
  handleSubmit: () => {
    /* noop */
  },
};

const useMockHook = (useHook: any, mockData: unknown) => {
  if (process.env.STORYBOOK_HOOK_MOCKS) return (_: any) => mockData;
  else return useHook;
};

export default useMockHook(useCreateRuleModalLogic, mockData);
